import { Link } from "react-router-dom";
import { useState } from "react";
import { MapPin, Clock, User, Flag, Repeat, Copy, Check, Navigation, BadgeCheck, AlertTriangle, Hourglass } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { tint, shade } from "@/lib/color";
import { Highlight } from "@/lib/highlight";

const statusLabel = { scheduled: "Scheduled", in_progress: "In progress", completed: "Completed", finalised: "Finalised", cancelled: "Cancelled" };

function formatDT(iso) {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// A job is "past due" when its scheduled window (start + duration) has ended
// AND it hasn't been progressed to completed / finalised / cancelled. Pipeline
// entries have no date so they're never past due.
function isPastDue(job) {
    if (!job || !job.scheduled_at || job.is_pipeline || job.is_wip) return false;
    if (["completed", "finalised", "cancelled"].includes(job.status)) return false;
    const start = new Date(job.scheduled_at).getTime();
    if (Number.isNaN(start)) return false;
    const endMs = start + Math.max(0, Number(job.duration_minutes) || 0) * 60_000;
    return Date.now() > endMs;
}

export default function JobCard({ job, showAssignee = true, clientColor, query = "", onUpdate }) {
    const [copied, setCopied] = useState(false);
    const [finalising, setFinalising] = useState(false);
    const { user } = useAuth();
    const isManager = user?.role === "manager";
    const isOwner = !!user?.is_workspace_owner;

    const finalise = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (finalising) return;
        setFinalising(true);
        try {
            const { data } = await api.patch(`/jobs/${job.id}`, { status: "finalised" });
            toast.success("Marked as finalised");
            if (onUpdate) onUpdate(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setFinalising(false); }
    };

    const copyAddress = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!job.address) return;
        try {
            await navigator.clipboard.writeText(job.address);
            setCopied(true);
            toast.success("Address copied — paste it into Google Maps");
            setTimeout(() => setCopied(false), 1600);
        } catch (_) {
            toast.error("Couldn't copy — long-press the address to select it");
        }
    };

    const color = clientColor || null;
    const pastDue = isPastDue(job);
    const style = color
        ? { background: tint(color, 0.12), borderColor: tint(color, 0.5) }
        : undefined;
    const assigneeColor = job.assignee_color || "";
    const helperNames = job.helper_names || [];
    const helperColors = job.helper_colors || [];

    return (
        <Link
            to={`/jobs/${job.id}`}
            data-testid={`job-card-${job.id}`}
            data-past-due={pastDue ? "true" : "false"}
            className={`card-flat block p-5 hover:shadow-md transition-all group relative overflow-hidden ${
                pastDue ? "ring-1 ring-amber-300/70 shadow-[inset_0_0_0_9999px_rgba(251,191,36,0.06)]" : ""
            }`}
            style={style}
        >
            {color && (
                <span
                    aria-hidden="true"
                    className="absolute left-0 top-0 bottom-0 w-1.5"
                    style={{ background: color }}
                />
            )}
            {assigneeColor && (
                <span
                    aria-hidden="true"
                    data-testid={`assignee-triangle-${job.id}`}
                    className="pointer-events-none absolute top-0 right-0"
                    style={{
                        width: 0,
                        height: 0,
                        borderTop: `18px solid ${assigneeColor}`,
                        borderLeft: "18px solid transparent",
                    }}
                    title={job.assigned_to_name || ""}
                />
            )}
            {job.status === "finalised" && (
                <span
                    data-testid={`finalised-badge-${job.id}`}
                    aria-hidden
                    className="pointer-events-none absolute top-2 right-2 h-5 w-5 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-sm ring-2 ring-white z-10"
                    title="Finalised — paperwork done"
                >
                    <Check size={11} strokeWidth={3} />
                </span>
            )}
            {job.status === "completed" && (
                <span
                    data-testid={`completed-badge-${job.id}`}
                    aria-hidden
                    className="pointer-events-none absolute top-2 right-2 h-5 w-5 rounded-full bg-[var(--pine)] text-white flex items-center justify-center shadow-sm ring-2 ring-white z-10"
                    title="Completed"
                >
                    <Check size={11} strokeWidth={3} />
                </span>
            )}
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`pill status-${job.status}`}>{statusLabel[job.status] || job.status}</span>
                        {pastDue && (
                            <span
                                data-testid={`past-due-pill-${job.id}`}
                                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border border-amber-300 bg-amber-50 text-amber-800"
                                title="Scheduled time has passed and this job isn't completed yet"
                            >
                                <AlertTriangle size={12} /> Past due
                            </span>
                        )}
                        {job.priority === "high" && (
                            <span className="pill priority-high"><Flag size={12} />High</span>
                        )}
                        {job.wip_copy_id && job.status !== "finalised" && (
                            <span data-testid={`wip-pill-${job.id}`} title="Also on the Work in progress list"
                                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border border-amber-300 bg-amber-50 text-amber-800">
                                <Hourglass size={12} /> Also in WIP
                            </span>
                        )}
                        {job.recurrence_days > 0 && (
                            <span className="pill" style={{ background: "var(--pine-light)", color: "var(--pine)" }}>
                                <Repeat size={12} /> Recurring
                            </span>
                        )}
                        {isOwner && job.status !== "finalised" && (
                            <button
                                type="button"
                                data-testid={`finalise-btn-${job.id}`}
                                onClick={finalise}
                                disabled={finalising}
                                title="Mark this job as finalised (paperwork done)"
                                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
                            >
                                <BadgeCheck size={12} />
                                {finalising ? "Marking…" : "Finalise"}
                            </button>
                        )}
                        {job.clocked_in_at && !job.clocked_out_at && (
                            <span className="pill status-in_progress inline-flex items-center gap-1" data-testid="on-site-badge">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-500 opacity-60"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                                </span>
                                On site
                            </span>
                        )}
                    </div>
                    <h3
                        className="font-display font-semibold text-lg mt-2 truncate transition-colors flex items-center gap-2"
                        style={color ? { color: shade(color, 0.55) } : undefined}
                    >
                        <MapPin size={16} className="shrink-0" style={color ? { color: shade(color, 0.55) } : undefined} />
                        <span className="truncate">
                            {job.address
                                ? <Highlight text={job.address} query={query} />
                                : <span className="text-[var(--text-muted)] italic">No address</span>}
                        </span>
                        {job.address && (
                            <div className="flex items-center gap-0.5 shrink-0" onClick={e => { e.preventDefault(); e.stopPropagation(); }}>
                                <button
                                    type="button"
                                    data-testid={`copy-address-${job.id}`}
                                    onClick={copyAddress}
                                    title={copied ? "Copied!" : "Copy address"}
                                    aria-label="Copy address"
                                    className={`p-1 rounded-md transition-colors ${
                                        copied
                                            ? "text-[var(--success)]"
                                            : "text-[var(--text-muted)] hover:text-[var(--pine)] hover:bg-[var(--pine-light)]"
                                    }`}
                                >
                                    {copied ? <Check size={14} /> : <Copy size={14} />}
                                </button>
                                <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.address)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`route-address-${job.id}`}
                                    onClick={e => e.stopPropagation()}
                                    title="Get directions in Google Maps"
                                    aria-label="Get directions"
                                    className="p-1 rounded-md text-[var(--text-muted)] hover:text-[var(--pine)] hover:bg-[var(--pine-light)] transition-colors"
                                >
                                    <Navigation size={14} />
                                </a>
                            </div>
                        )}
                    </h3>
                    <div className="mt-1 text-sm text-[var(--text-secondary)] truncate flex items-center gap-1.5">
                        {color && <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />}
                        <Highlight text={job.client_name} query={query} />
                    </div>
                    <div className="mt-1 text-sm font-medium text-[var(--text-primary)] truncate">
                        <Highlight text={job.title} query={query} />
                    </div>
                </div>
                <div className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] shrink-0 hidden sm:block">
                    {job.duration_minutes} min
                </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-[var(--text-secondary)]">
                <div className="inline-flex items-center gap-2">
                    <Clock size={14} className="text-[var(--pine)]" />
                    <span>{formatDT(job.scheduled_at)}</span>
                </div>
                {showAssignee && (
                    <div className="inline-flex items-center gap-2 flex-wrap">
                        <User size={14} className="text-[var(--pine)]" />
                        <span>{job.assigned_to_name ? <Highlight text={job.assigned_to_name} query={query} /> : <span className="text-[var(--text-muted)]">Unassigned</span>}</span>
                        {helperNames.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs text-[var(--text-muted)]">
                                + {helperNames.map((n, i) => (
                                    <span key={i} className="inline-flex items-center gap-1">
                                        {helperColors[i] && <span className="inline-block h-2 w-2 rounded-full" style={{ background: helperColors[i] }} />}
                                        {n}{i < helperNames.length - 1 ? "," : ""}
                                    </span>
                                ))}
                            </span>
                        )}
                    </div>
                )}
                {job.checklist?.length > 0 && (
                    <div className="text-xs text-[var(--text-muted)]">
                        {job.checklist_done?.length || 0} / {job.checklist.length} checklist items
                    </div>
                )}
            </div>
        </Link>
    );
}
